import React, { useState } from 'react';
import {
  Loader2,
  Plus,
  FileText,
  X,
  PencilLine,
  Archive,
  Layers,
  Settings2,
  Replace,
  ArrowUpFromLine,
  ArrowDownToLine,
  RotateCcw,
  ChevronDown,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-toastify';

import dayjs from '../../lib/day';
import { cn } from '../../lib/utils';
import PrivateLayout from '../layouts/PrivateLayout';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  useFetchDocumentsGrouped,
  useCreateTemplate,
  useArchiveTemplate,
  useRestoreTemplate,
  useDeleteTemplate,
  exportTemplateBundle,
} from '../../services/documents';
import DocumentTemplateBuilder from './DocumentTemplateBuilder';
import { ManageDocumentsModal } from './ProcessModals';
import { ImportDocumentModal, SwapDocumentModal, FilePicker } from './TemplatePortabilityModals';
import useEscapeToClose from './useEscapeToClose';

// Per-process accent colors (categorical) — gives each pipeline group a distinct
// identity (left bar + icon tile) so the page doesn't read flat.
const PROCESS_ACCENT = {
  partner_onboarding: { bar: 'border-l-blue-500', tile: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400' },
  internal_onboarding: { bar: 'border-l-violet-500', tile: 'bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400' },
  client_onboarding: { bar: 'border-l-teal-500', tile: 'bg-teal-100 text-teal-600 dark:bg-teal-500/15 dark:text-teal-400' },
  provider_onboarding: { bar: 'border-l-amber-500', tile: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400' },
};
const DEFAULT_ACCENT = { bar: 'border-l-slate-400', tile: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400' };

const NewTemplateModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [file, setFile] = useState(null);
  const create = useCreateTemplate();

  useEscapeToClose(onClose);

  const submit = async () => {
    if (!name.trim() || !file) {
      toast.error('A name and a PDF file are required.');
      return;
    }
    try {
      const template = await create.mutateAsync({ file, name: name.trim() });
      toast.success('Template created.');
      onCreated(template.documentId);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Upload failed.');
    }
  };

  return (
    <div className="fixed inset-0 z-[1060] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-base font-semibold text-foreground">New document</h3>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. IRS W-9" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">PDF file</label>
            <FilePicker accept="application/pdf" file={file} onPick={setFile} />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} disabled={create.isLoading} className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700">
              Cancel
            </button>
            <Button onClick={submit} disabled={create.isLoading} className="hover:bg-primary hover:opacity-100">
              {create.isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Create + place fields
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children, className }) => (
  <th className={cn('px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-muted-foreground', className)}>
    {children}
  </th>
);

const DocumentTable = ({ documents, ordered, onEdit, onArchive, onExport, onSwap }) => (
  <table className="w-full table-fixed text-sm">
    <thead>
      <tr className="border-b border-border">
        <Th className="w-[5%]">{ordered ? '#' : ''}</Th>
        <Th className="w-[33%]">Document</Th>
        <Th className="w-[16%] text-center">Pages</Th>
        <Th className="w-[20%] text-center">Updated</Th>
        <Th className="w-[26%] text-center">Actions</Th>
      </tr>
    </thead>
    <tbody>
      {documents.map((t, i) => (
        <tr key={t.documentId} className="border-b border-border last:border-0 hover:bg-neutral-50 dark:hover:bg-white/5">
          <td className="w-10 px-4 py-3 text-muted-foreground">{ordered ? i + 1 : ''}</td>
          <td className="px-4 py-3">
            <button type="button" onClick={() => onEdit(t.documentId)} className="flex items-center gap-2 font-medium text-foreground hover:text-primary">
              <FileText className="h-4 w-4 text-muted-foreground" />
              {t.name}
            </button>
          </td>
          <td className="px-4 py-3 text-center text-muted-foreground">{t.pageCount || '—'}</td>
          <td className="px-4 py-3 text-center text-muted-foreground">{t.updatedAt ? dayjs(t.updatedAt).format('MMM D, YYYY') : '—'}</td>
          <td className="px-4 py-3">
            <div className="flex items-center justify-center gap-1">
              <button type="button" onClick={() => onExport(t)} title="Export (.json)" className="rounded-md p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary">
                <ArrowUpFromLine className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => onSwap(t)} title="Replace" className="rounded-md p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary">
                <Replace className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => onEdit(t.documentId)} title="Edit fields" className="rounded-md p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary">
                <PencilLine className="h-4 w-4" />
              </button>
              {/* Archive is reversible — neutral at rest, warning (not destructive) on hover. */}
              <button type="button" onClick={() => onArchive(t)} title="Archive" className="rounded-md p-1.5 text-muted-foreground hover:bg-status-warning-bg hover:text-status-warning-fg">
                <Archive className="h-4 w-4" />
              </button>
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const DocumentsPage = () => {
  const [editingId, setEditingId] = useState(null);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [managing, setManaging] = useState(null);
  const [swapping, setSwapping] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const { data, isLoading } = useFetchDocumentsGrouped();
  const archive = useArchiveTemplate();
  const restore = useRestoreTemplate();
  const del = useDeleteTemplate();

  const processes = data?.processes || [];
  const uncategorized = data?.uncategorized || [];
  const archived = data?.archived || [];

  // The archive/delete confirms are rendered inline (not as child components),
  // so their Escape listeners attach here, only while one is open.
  useEscapeToClose(() => setConfirmingArchive(null), Boolean(confirmingArchive));
  useEscapeToClose(() => setConfirmingDelete(null), Boolean(confirmingDelete));

  const doArchive = async () => {
    if (!confirmingArchive) return;
    try {
      await archive.mutateAsync(confirmingArchive.documentId);
      toast.success('Document archived.');
      setConfirmingArchive(null);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not archive.');
    }
  };

  const handleRestore = async (documentId) => {
    try {
      await restore.mutateAsync(documentId);
      toast.success('Document restored.');
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not restore.');
    }
  };

  const doDelete = async () => {
    if (!confirmingDelete) return;
    try {
      await del.mutateAsync(confirmingDelete.documentId);
      toast.success('Document deleted.');
      setConfirmingDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Could not delete.');
    }
  };

  const handleExport = async (t) => {
    try {
      await exportTemplateBundle(t.documentId, t.name);
      toast.success('Export downloaded.');
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Export failed.');
    }
  };

  return (
    <PrivateLayout>
      {editingId ? (
        <DocumentTemplateBuilder documentId={editingId} onBack={() => setEditingId(null)} />
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Documents</h1>
              <p className="text-sm text-muted-foreground">
                Upload documents and assign them to a pipeline group, then send them for signature.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShowImport(true)} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                <ArrowDownToLine className="h-4 w-4" /> Import
              </button>
              <Button onClick={() => setShowNewTemplate(true)} className="hover:bg-primary hover:opacity-100">
                <Plus className="mr-2 h-4 w-4" /> New Document
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="space-y-5">
              {processes.map((p) => {
                const accent = PROCESS_ACCENT[p.purpose] || DEFAULT_ACCENT;
                const hasDocs = p.documents.length > 0;
                return (
                  <div key={p.processId} className={cn('overflow-hidden rounded-xl border border-l-4 border-border bg-card', accent.bar)}>
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', accent.tile)}>
                          <Layers className="h-[18px] w-[18px]" />
                        </span>
                        <div>
                          <div className="font-semibold leading-tight text-foreground">{p.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {hasDocs
                              ? `${p.documents.length} document${p.documents.length === 1 ? '' : 's'}`
                              : 'No documents here yet'}
                          </div>
                        </div>
                      </div>
                      <button type="button" onClick={() => setManaging(p)} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent">
                        <Settings2 className="h-4 w-4" /> Manage
                      </button>
                    </div>
                    {hasDocs ? (
                      <DocumentTable documents={p.documents} ordered onEdit={setEditingId} onArchive={setConfirmingArchive} onExport={handleExport} onSwap={setSwapping} />
                    ) : null}
                  </div>
                );
              })}

              {uncategorized.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                  <div className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
                    Uncategorized
                  </div>
                  <DocumentTable documents={uncategorized} onEdit={setEditingId} onArchive={setConfirmingArchive} onExport={handleExport} onSwap={setSwapping} />
                </div>
              ) : null}

              {archived.length > 0 ? (
                <div className="overflow-hidden rounded-xl border border-border bg-card">
                  <button
                    type="button"
                    onClick={() => setArchivedOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-muted-foreground hover:bg-accent/50"
                  >
                    <span>Archived <span className="font-normal">· {archived.length}</span></span>
                    <ChevronDown className={cn('h-4 w-4 transition-transform', archivedOpen ? '' : '-rotate-90')} />
                  </button>
                  {archivedOpen ? (
                  <table className="w-full border-t border-border text-sm">
                    <tbody>
                      {archived.map((t) => (
                        <tr key={t.documentId} className="border-b border-border last:border-0">
                          <td className="px-4 py-3">
                            <span className="flex items-center gap-2 text-muted-foreground">
                              <FileText className="h-4 w-4" />
                              {t.name}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button type="button" onClick={() => handleRestore(t.documentId)} className="inline-flex items-center gap-1.5 rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent">
                                <RotateCcw className="h-4 w-4" /> Restore
                              </button>
                              <button type="button" onClick={() => setConfirmingDelete(t)} title="Delete permanently" className="rounded-md p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </div>
      )}

      {showNewTemplate ? (
        <NewTemplateModal
          onClose={() => setShowNewTemplate(false)}
          onCreated={(documentId) => {
            setShowNewTemplate(false);
            setEditingId(documentId);
          }}
        />
      ) : null}

      {managing ? (
        <ManageDocumentsModal
          process={managing}
          uncategorized={uncategorized}
          onClose={() => setManaging(null)}
        />
      ) : null}

      {showImport ? (
        <ImportDocumentModal
          onClose={() => setShowImport(false)}
          onImported={(documentId) => {
            setShowImport(false);
            setEditingId(documentId);
          }}
        />
      ) : null}

      {swapping ? (
        <SwapDocumentModal
          document={swapping}
          onClose={() => setSwapping(null)}
          onSwapped={() => setSwapping(null)}
        />
      ) : null}

      {confirmingArchive ? (
        <div className="fixed inset-0 z-[1060] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmingArchive(null)} aria-hidden="true" />
          <div className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-base font-semibold text-foreground">Archive document</h3>
              <button type="button" onClick={() => setConfirmingArchive(null)} className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 p-4">
              <p className="text-sm text-muted-foreground">
                Archive <span className="font-medium text-foreground">{confirmingArchive.name}</span>? It will be
                hidden from the pipeline. You can restore it anytime from the Archived section.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setConfirmingArchive(null)} disabled={archive.isLoading} className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700">
                  Cancel
                </button>
                {/* Reversible action — neutral at rest, warning on hover (not destructive red). */}
                <button type="button" onClick={doArchive} disabled={archive.isLoading} className="inline-flex items-center gap-2 rounded-md border border-input bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-status-warning-bg hover:text-status-warning-fg disabled:opacity-50">
                  {archive.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                  Archive
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {confirmingDelete ? (
        <div className="fixed inset-0 z-[1060] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmingDelete(null)} aria-hidden="true" />
          <div className="relative z-10 w-full max-w-sm rounded-xl border border-border bg-card shadow-lg">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-base font-semibold text-foreground">Delete permanently</h3>
              <button type="button" onClick={() => setConfirmingDelete(null)} className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4 p-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  Permanently delete <span className="font-medium text-foreground">{confirmingDelete.name}</span> and its PDF?
                </p>
                <p className="text-sm font-medium text-rose-600">This can&apos;t be undone.</p>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setConfirmingDelete(null)} disabled={del.isLoading} className="rounded-md bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-200 disabled:opacity-50 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700">
                  Cancel
                </button>
                <button type="button" onClick={doDelete} disabled={del.isLoading} className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">
                  {del.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete permanently
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PrivateLayout>
  );
};

export default DocumentsPage;
